import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, AlertCircle, Flame, AlarmClock, Activity as ActivityIcon, Mail, Workflow, FileSpreadsheet, ShieldCheck, Settings, Users as UsersIcon, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { fetchProperty, fetchInvestorStats, fetchHotLeads, fetchSweetSpot, fetchNdaReminders, listActivity, fetchMailtoDraft, markReminderSent } from "../lib/api";
import { fmtEur, fmtQm, fmtDate } from "../lib/format";
import { PHASES, BETREUER, PhaseBadge } from "../components/investors/Badges";
import BerichtModal from "../components/reports/BerichtModal";
import PageHeader from "../components/layout/PageHeader";

const ACT_ICONS = {
  kontakthistorie_seed: ShieldCheck,
  kontakthistorie_neu: Mail,
  investor_phase_change: Workflow,
  investor_update: Settings,
  investor_sperre: ShieldCheck,
  investor_dokument_status: FileSpreadsheet,
  investor_datenraum: FileSpreadsheet,
  dokument_upload: FileSpreadsheet,
  dokument_delete: Trash2,
  bulk_update: UsersIcon,
  nda_reminder_sent: Mail,
};
const ACT_LABELS = {
  kontakthistorie_seed: "Kontakt-Seed",
  kontakthistorie_neu: "Kontakt-Eintrag",
  investor_phase_change: "Phase geändert",
  investor_update: "Investor geändert",
  investor_sperre: "Sperre",
  investor_dokument_status: "Dokument-Status",
  investor_datenraum: "Datenraum",
  dokument_upload: "Dokument hochgeladen",
  dokument_delete: "Dokument entfernt",
  bulk_update: "Bulk-Update",
  nda_reminder_sent: "NDA-Erinnerung",
};
const fmtRel = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const KpiCard = ({ label, value, sub, testid, accent }) => (
  <div className="pb-kpi animate-fade-in-up" data-testid={testid}>
    <div className="pb-eyebrow">{label}</div>
    <div className={`mt-3 font-serif text-3xl ${accent ? "text-gold" : "text-navy"} leading-none`}>{value}</div>
    {sub && <div className="mt-2 text-xs text-navy/55 font-sans">{sub}</div>}
  </div>
);

const totalJahresmiete = (mieter = []) =>
  mieter.reduce((s, m) => s + (m.miete_pa_eur || 0), 0);

const parseFestLaufzeitJahre = (text) => {
  if (!text) return 0;
  const base = text.split("+")[0];
  const j = base.match(/(\d+)\s*J/);
  const m = base.match(/(\d+)\s*M/);
  return (j ? parseInt(j[1], 10) : 0) + (m ? parseInt(m[1], 10) : 0) / 12;
};
const waultJahre = (m = []) => {
  const t = totalJahresmiete(m);
  return t ? m.reduce((s, x) => s + parseFestLaufzeitJahre(x.wault_text || "") * (x.miete_pa_eur || 0), 0) / t : 0;
};

// --- Funnel ---
const FunnelBar = ({ phase, count, max, onClick }) => {
  const pct = max ? (count / max) * 100 : 0;
  return (
    <button
      onClick={onClick}
      data-testid={`funnel-${phase.toLowerCase()}`}
      className="block w-full text-left group"
    >
      <div className="flex items-baseline justify-between text-xs font-sans mb-1">
        <span className="text-navy/70 group-hover:text-navy uppercase tracking-widish">{phase}</span>
        <span className="text-navy tabular-nums font-medium">{count}</span>
      </div>
      <div className="h-2 bg-ledger relative overflow-hidden">
        <div
          className="h-full transition-all duration-500 group-hover:opacity-90"
          style={{
            width: `${pct}%`,
            backgroundColor: phase === "Abgesagt" ? "#9CA3AF" : phase === "Gebot" || phase === "Verhandlung" ? "#C4922A" : "#022750",
          }}
        />
      </div>
    </button>
  );
};

const HotLeadsModal = ({ open, onClose, items }) => {
  if (!open) return null;
  const today = new Date();
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/30 z-40" />
      <div data-testid="hotleads-modal" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(720px,95vw)] max-h-[80vh] bg-white border border-navy/15 z-50 flex flex-col">
        <div className="border-b border-navy/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="pb-eyebrow">Hot Leads</div>
            <h3 className="font-serif text-navy text-2xl mt-1 flex items-center gap-2">
              <Flame size={20} className="text-gold" /> NDA-Unterzeichner ohne Aktivität
            </h3>
          </div>
          <button onClick={onClose} className="text-navy/60 hover:text-navy">✕</button>
        </div>
        <div className="overflow-y-auto">
          <table className="pb-table">
            <thead>
              <tr><th>Firma</th><th>Phase</th><th>NDA seit</th><th>Tage</th><th></th></tr>
            </thead>
            <tbody>
              {items?.map((inv) => {
                const since = inv.letztes_aktivitaetsdatum ? new Date(inv.letztes_aktivitaetsdatum) : null;
                const days = since ? Math.floor((today - since) / (1000 * 60 * 60 * 24)) : "—";
                return (
                  <tr key={inv.id} data-testid={`hotlead-row-${inv.id}`}>
                    <td className="text-navy font-medium">{inv.firma}</td>
                    <td><PhaseBadge phase={inv.phase} /></td>
                    <td>{fmtDate(inv.nda_unterzeichnet_am)}</td>
                    <td className="text-gold tabular-nums font-medium">{days}</td>
                    <td>
                      <Link
                        to={`/investoren?search=${encodeURIComponent(inv.firma)}`}
                        className="text-xs uppercase tracking-widish text-navy hover:text-gold inline-flex items-center gap-1"
                        onClick={onClose}
                      >
                        Öffnen <ArrowUpRight size={12}/>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// Mailto-Modal für NDA-Reminder (analog Mailto im InvestorDrawer)
const ReminderMailModal = ({ investor, onClose, onSent }) => {
  const [preview, setPreview] = useState(null);
  const [mailtoUrl, setMailtoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    if (!investor) return;
    setLoading(true);
    fetchMailtoDraft(investor.id, "nda_reminder")
      .then((d) => { setPreview(d.preview); setMailtoUrl(d.mailto_url); })
      .catch(() => toast.error("Mail-Vorlage konnte nicht geladen werden"))
      .finally(() => setLoading(false));
  }, [investor]);

  if (!investor) return null;

  const handleSend = async () => {
    if (!mailtoUrl) return;
    setMarking(true);
    try {
      await markReminderSent(investor.id);
    } catch {
      // Markierung darf den Mailflow nicht blockieren
    }
    setMarking(false);
    window.location.href = mailtoUrl;
    onSent?.();
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/30 z-40" />
      <div
        data-testid="reminder-mail-modal"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(720px,95vw)] max-h-[85vh] bg-white border border-navy/15 z-50 flex flex-col"
      >
        <div className="border-b border-navy/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="pb-eyebrow">NDA-Erinnerung</div>
            <h3 className="font-serif text-navy text-2xl mt-1 break-words pr-8">{investor.firma}</h3>
          </div>
          <button onClick={onClose} data-testid="reminder-mail-close" className="text-navy/60 hover:text-navy">✕</button>
        </div>

        {loading || !preview ? (
          <div className="p-8 text-navy/55 font-sans">Lade Vorlage …</div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 text-sm font-sans">
            <div>
              <div className="pb-label">An</div>
              <div className="text-navy mt-1 break-all">{preview.to || <span className="text-navy/45">— keine E-Mail-Adresse hinterlegt —</span>}</div>
            </div>
            <div>
              <div className="pb-label">Betreff</div>
              <div className="text-navy mt-1">{preview.subject}</div>
            </div>
            <div>
              <div className="pb-label">Nachricht</div>
              <pre data-testid="reminder-mail-body" className="text-navy mt-1 whitespace-pre-wrap font-sans bg-ledger/40 border border-navy/10 p-4 text-[13px] leading-relaxed">
                {preview.body}
              </pre>
            </div>
            <div className="text-xs text-navy/55 border-t border-navy/10 pt-3">
              Hinweis: Mit Klick auf „Mail-Client öffnen" wird die Vorlage in deinem Standard-Mailprogramm (Outlook etc.) geöffnet.
              Gleichzeitig wird der Eintrag in der Kontakthistorie des Investors als „NDA-Erinnerung versandt" protokolliert.
            </div>
          </div>
        )}

        <div className="border-t border-navy/10 px-6 py-4 flex items-center justify-between gap-3">
          <Link
            to={`/investoren?search=${encodeURIComponent(investor.firma)}`}
            className="text-xs uppercase tracking-widish text-navy/65 hover:text-navy inline-flex items-center gap-1.5 font-sans"
            onClick={onClose}
            data-testid="reminder-mail-open-investor"
          >
            Investor öffnen <ExternalLink size={12}/>
          </Link>
          <div className="flex gap-2">
            <button onClick={onClose} className="pb-btn-ghost">Abbrechen</button>
            <button
              onClick={handleSend}
              disabled={loading || !mailtoUrl || marking}
              data-testid="reminder-mail-send"
              className="pb-btn-primary gap-2"
            >
              <Mail size={13}/> {marking ? "…" : "Mail-Client öffnen"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default function Uebersicht() {
  const [property, setProperty] = useState(null);
  const [stats, setStats] = useState(null);
  const [hot, setHot] = useState([]);
  const [sweetSpot, setSweetSpot] = useState([]);
  const [ndaReminders, setNdaReminders] = useState({ total: 0, items: [] });
  const [activities, setActivities] = useState([]);
  const [hotOpen, setHotOpen] = useState(false);
  const [sweetOpen, setSweetOpen] = useState(false);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [betreuerFilter, setBetreuerFilter] = useState("");
  const [statsLoading, setStatsLoading] = useState(false);

  // Initial load
  useEffect(() => {
    Promise.all([
      fetchProperty(),
      fetchInvestorStats(),
      fetchHotLeads(20),
      fetchSweetSpot(5),
      fetchNdaReminders(14),
      listActivity({ page: 1, page_size: 8 }),
    ])
      .then(([p, s, h, ss, nda, act]) => {
        setProperty(p); setStats(s); setHot(h); setSweetSpot(ss);
        setNdaReminders(nda || { total: 0, items: [] });
        setActivities(act?.items || []);
      })
      .catch((e) => setErr(e?.message || "Laden fehlgeschlagen"))
      .finally(() => setLoading(false));
  }, []);

  const [berichtOpen, setBerichtOpen] = useState(false);

  // Funnel-Filter (Betreuer): only refetch stats
  const reloadStats = useCallback(async (b) => {
    setStatsLoading(true);
    try {
      const s = await fetchInvestorStats(b || undefined);
      setStats(s);
    } finally { setStatsLoading(false); }
  }, []);

  useEffect(() => {
    if (!loading) reloadStats(betreuerFilter);
  }, [betreuerFilter, loading, reloadStats]);

  if (loading) return <div className="py-24 text-center text-navy/50 font-sans" data-testid="uebersicht-loading">Lade Daten …</div>;
  if (err || !property || !stats) {
    return (
      <div className="pb-card flex items-center gap-3 text-navy/80" data-testid="uebersicht-error">
        <AlertCircle size={18} className="text-gold" /> Daten konnten nicht geladen werden.
      </div>
    );
  }

  const jahresmiete = totalJahresmiete(property.mieter);
  const wault = waultJahre(property.mieter);
  const waultStr = wault ? `${Math.floor(wault)}J ${Math.round((wault - Math.floor(wault)) * 12)}M` : "—";

  const funnelMax = Math.max(1, ...Object.values(stats.by_phase || {}));

  return (
    <div data-testid="uebersicht-page" className="space-y-12">
      {/* Hero */}
      <PageHeader
        eyebrow="Newton Campus · Übersicht"
        title={property.name}
        subtitle={`${property.adresse_strasse} · ${property.adresse_plz} ${property.adresse_ort} · ${property.land}`}
        testid="uebersicht-header"
        actions={
          <>
            <button
              data-testid="uebersicht-bericht-btn"
              onClick={() => setBerichtOpen(true)}
              className="pb-btn-accent text-xs uppercase tracking-widish font-semibold min-h-[40px] gap-2"
              title="Excel-Komplettexport aller Investoren"
            >
              <FileSpreadsheet size={14} strokeWidth={1.6} /> Investoren-Export
            </button>
            <Link to="/objekt" data-testid="uebersicht-cta-objekt" className="pb-btn-outline text-xs uppercase tracking-widish font-semibold min-h-[40px] gap-2">
              Stammdaten <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </>
        }
      />

      {/* Objekt-KPIs */}
      <section data-testid="uebersicht-kpis" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-jahresmiete" label="Jahresmiete (IST)" value={fmtEur(jahresmiete)} sub="Summe aller Mietverträge" />
        <KpiCard testid="kpi-wault" label="WAULT (gewichtet)" value={waultStr} sub="Gewichtet nach Jahresmiete" />
        <KpiCard testid="kpi-mietflaeche" label="Mietfläche" value={fmtQm(property.mietflaeche_qm)} sub={`BGF gesamt: ${fmtQm(property.bgf_gesamt_qm)}`} />
        <KpiCard testid="kpi-mieteranzahl" label="Mieter" value={String(property.mieter?.length || 0)} sub="Aktive Mietverhältnisse" />
      </section>

      {/* Investoren-KPIs */}
      <section className="space-y-4">
        <div className="flex items-end justify-between">
          <h2 className="pb-section-title mb-0">Investoren-Akquise</h2>
          <Link to="/investoren" className="text-xs uppercase tracking-widish text-navy hover:text-gold inline-flex items-center gap-1.5 font-sans">
            Alle Investoren <ArrowUpRight size={12}/>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/investoren" className="block">
            <KpiCard testid="kpi-investoren-total" label="Investoren gesamt" value={stats.total}
              sub={`davon BNP-Datenbasis: ${stats.by_quelle?.BNP ?? 0} · gesperrt: ${stats.gesperrt_fuer_bnp + stats.gesperrt_fuer_uns}`} />
          </Link>
          <button onClick={() => setHotOpen(true)} className="block text-left" data-testid="kpi-hotleads-btn">
            <div className="pb-kpi border-l-gold cursor-pointer">
              <div className="pb-eyebrow flex items-center gap-1.5"><Flame size={12} className="text-gold"/> Hot Leads</div>
              <div className="mt-3 font-serif text-3xl text-gold leading-none">{stats.hot_leads}</div>
              <div className="mt-2 text-xs text-navy/55 font-sans">
                {stats.hot_leads === 0 ? "Keine Hot Leads aktuell" : "NDA-Unterzeichner ≥ 30 Tage ohne Aktivität"}
              </div>
            </div>
          </button>
          <Link to="/investoren?auch_bei_bnp=true" className="block">
            <KpiCard testid="kpi-auch-bnp" label="Auch bei BNP" value={stats.auch_bei_bnp}
              sub="Listenchampion ∩ BNP-Reporting" />
          </Link>
          <Link to="/investoren?phase=Abgesagt" className="block">
            <KpiCard testid="kpi-abgesagt" label="Absagen" value={stats.by_phase?.Abgesagt ?? 0}
              sub="Aus BNP-Reporting" />
          </Link>
        </div>
      </section>

      {/* Funnel */}
      <section data-testid="funnel-section">
        <div className="flex items-end justify-between mb-2">
          <h2 className="pb-section-title mb-0">Pipeline (Lese-Ansicht)</h2>
          <div className="flex items-center gap-2">
            <label className="pb-eyebrow">Betreuer</label>
            <select
              data-testid="funnel-betreuer-filter"
              value={betreuerFilter}
              onChange={(e) => setBetreuerFilter(e.target.value)}
              className="text-sm font-sans border border-navy/20 px-3 py-1 bg-white text-navy focus:outline-none focus:border-gold"
            >
              <option value="">Alle</option>
              {BETREUER.map((b) => <option key={b}>{b}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3" style={{opacity: statsLoading ? 0.5 : 1, transition: "opacity 0.15s"}}>
          {PHASES.map((p) => (
            <FunnelBar
              key={p}
              phase={p}
              count={stats.by_phase?.[p] ?? 0}
              max={funnelMax}
              onClick={() => window.location.assign(`/investoren?phase=${encodeURIComponent(p)}${betreuerFilter ? `&betreuer=${encodeURIComponent(betreuerFilter)}` : ""}`)}
            />
          ))}
        </div>
        <p className="mt-6 text-xs text-navy/45 font-sans">
          Detail-Filter und Investor-Listen pro Phase: siehe <Link to="/investoren" className="hover:text-gold">Investoren-Übersicht</Link>.
        </p>
      </section>

      {/* Sweet-Spot-Kachel */}
      <section data-testid="sweet-spot-section" className="space-y-4">
        <div className="flex items-end justify-between">
          <h2 className="pb-section-title mb-0">Top-Kandidaten Heinrichs</h2>
          <span className="pb-eyebrow">Immobilien · AuM ≥ 100 Mio €</span>
        </div>
        {sweetSpot.length === 0 ? (
          <div className="pb-placeholder">Aktuell keine Kandidaten gefunden.</div>
        ) : (
          <div className="border border-navy/15">
            <table className="pb-table" data-testid="sweet-spot-table">
              <thead>
                <tr>
                  <th>Firma</th>
                  <th>Stadt</th>
                  <th className="text-right">AuM (Mio €)</th>
                  <th>Phase</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sweetSpot.map((s) => (
                  <tr key={s.id} data-testid={`sweet-spot-row-${s.id}`}>
                    <td className="text-navy font-medium">{s.firma}</td>
                    <td className="text-navy/70">{s.stadt || "—"}</td>
                    <td className="text-navy/85 tabular-nums text-right">{s.aum_text || (s.aum_mio_eur ? `${s.aum_mio_eur}` : "—")}</td>
                    <td><PhaseBadge phase={s.phase} /></td>
                    <td>
                      <Link
                        to={`/investoren?search=${encodeURIComponent(s.firma)}`}
                        className="text-xs uppercase tracking-widish text-navy hover:text-gold inline-flex items-center gap-1.5"
                      >
                        Öffnen <ArrowUpRight size={12}/>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* NDA-Reminder */}
      <section data-testid="nda-reminders-section" className="space-y-4">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="pb-section-title mb-1 flex items-center gap-2">
              <AlarmClock size={18} className="text-gold" strokeWidth={1.6}/> NDA-Reminder
            </h2>
            <p className="text-xs text-navy/55 font-sans">
              NDA versandt &gt; 14 Tage · keine Unterschrift erhalten
            </p>
          </div>
          <Link
            to="/investoren?nda_offen=true"
            className="text-xs uppercase tracking-widish text-navy hover:text-gold inline-flex items-center gap-1.5 font-sans"
          >
            Alle anzeigen <ArrowUpRight size={12}/>
          </Link>
        </div>
        {ndaReminders.total === 0 ? (
          <div className="pb-placeholder">Aktuell keine überfälligen NDAs.</div>
        ) : (
          <div className="border border-navy/15">
            <div className="px-5 py-3 border-b border-navy/10 bg-ledger/40 flex items-center justify-between">
              <div className="text-sm font-sans text-navy">
                <span className="text-gold font-serif text-xl mr-2 tabular-nums">{ndaReminders.total}</span>
                offene NDA-Erinnerung(en)
              </div>
              <span className="pb-eyebrow">Top 5</span>
            </div>
            <table className="pb-table" data-testid="nda-reminders-table">
              <thead>
                <tr>
                  <th>Firma</th>
                  <th>Betreuer</th>
                  <th>NDA versandt</th>
                  <th className="text-right">Tage offen</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {ndaReminders.items.slice(0, 5).map((r) => (
                  <tr key={r.id} data-testid={`nda-reminder-row-${r.id}`}>
                    <td className="text-navy font-medium">{r.firma}</td>
                    <td>
                      <span className="text-[10px] uppercase tracking-widish px-2 py-0.5 bg-ledger border border-navy/20 text-navy/80 font-sans">
                        {r.betreuer_intern || "—"}
                      </span>
                    </td>
                    <td className="text-navy/70 tabular-nums">{fmtDate(r.nda_versandt_am)}</td>
                    <td className="text-gold tabular-nums font-medium text-right">{r.tage_seit_versand}</td>
                    <td>
                      <div className="flex items-center justify-end gap-3">
                        <button
                          data-testid={`nda-reminder-mail-${r.id}`}
                          onClick={() => setReminderTarget(r)}
                          className="text-xs uppercase tracking-widish text-gold hover:text-navy inline-flex items-center gap-1.5 font-medium"
                          title="Reminder-Mail vorbereiten"
                        >
                          <Mail size={12}/> Mail
                        </button>
                        <Link
                          data-testid={`nda-reminder-open-${r.id}`}
                          to={`/investoren?search=${encodeURIComponent(r.firma)}`}
                          className="text-xs uppercase tracking-widish text-navy/55 hover:text-navy inline-flex items-center gap-1.5"
                          title="Investor öffnen"
                        >
                          <ArrowUpRight size={12}/>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Aktivitäten-Feed */}
      <section data-testid="uebersicht-aktivitaeten" className="space-y-4">
        <div className="flex items-end justify-between">
          <h2 className="pb-section-title mb-0 flex items-center gap-2">
            <ActivityIcon size={18} className="text-navy/70" strokeWidth={1.6}/> Letzte Aktivitäten
          </h2>
          <Link
            to="/aktivitaeten"
            className="text-xs uppercase tracking-widish text-navy hover:text-gold inline-flex items-center gap-1.5 font-sans"
          >
            Vollständiges Audit-Log <ArrowUpRight size={12}/>
          </Link>
        </div>
        {activities.length === 0 ? (
          <div className="pb-placeholder">Noch keine Aktivitäten erfasst.</div>
        ) : (
          <ul className="border border-navy/15 divide-y divide-navy/10" data-testid="activity-feed">
            {activities.map((a) => {
              const Icon = ACT_ICONS[a.aktion_typ] || Settings;
              const label = ACT_LABELS[a.aktion_typ] || a.aktion_typ;
              const firstDetail = Object.entries(a.details || {})[0];
              return (
                <li key={a.id} className="flex items-start gap-4 px-5 py-3" data-testid={`feed-row-${a.id}`}>
                  <div className="mt-0.5 text-navy/65"><Icon size={14} strokeWidth={1.7} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-sans text-navy">
                      <span className="font-medium">{label}</span>
                      {a.ressource_name && (
                        <>
                          {" · "}
                          {a.ressource_typ === "investor" && a.ressource_id ? (
                            <Link to={`/investoren?search=${encodeURIComponent(a.ressource_name)}`} className="text-navy hover:text-gold">
                              {a.ressource_name}
                            </Link>
                          ) : (
                            <span className="text-navy/80">{a.ressource_name}</span>
                          )}
                        </>
                      )}
                    </div>
                    {firstDetail && (
                      <div className="text-xs text-navy/55 font-sans truncate mt-0.5">
                        <strong>{firstDetail[0]}:</strong>{" "}
                        {typeof firstDetail[1] === "object" ? JSON.stringify(firstDetail[1]) : String(firstDetail[1])}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs font-sans whitespace-nowrap">
                    <div className="text-[10px] uppercase tracking-widish px-2 py-0.5 bg-ledger border border-navy/20 text-navy/80 inline-block mb-1">
                      {a.akteur}
                    </div>
                    <div className="text-navy/55">{fmtRel(a.timestamp)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <HotLeadsModal open={hotOpen} onClose={() => setHotOpen(false)} items={hot} />
      <BerichtModal open={berichtOpen} onClose={() => setBerichtOpen(false)} />
      <ReminderMailModal
        investor={reminderTarget}
        onClose={() => setReminderTarget(null)}
        onSent={() => {
          // Reload nach kurzem Delay damit das Markieren auch ankommt
          setTimeout(async () => {
            try {
              const [nda, act] = await Promise.all([
                fetchNdaReminders(14),
                listActivity({ page: 1, page_size: 8 }),
              ]);
              setNdaReminders(nda || { total: 0, items: [] });
              setActivities(act?.items || []);
            } catch (e) { /* ignore */ }
          }, 500);
        }}
      />
    </div>
  );
}
